/**
 * iter 55 / 56 — Collaboration tab body.
 *
 * Sections:
 *   1. Your organization's code (display + copy)
 *   2. Send a partnership invite by email (inline: email + button) — uses
 *      the `collaboration_invite` template stored in Templates → Email.
 *   3. Redeem a partner's code (input + submit)
 *   4. Table of organizations collaborating with you (checkbox / name /
 *      city,state / last interaction / partnered since), with bulk revoke.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Copy, ArrowRight, Handshake, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";
import { Field } from "./UI";
import { useAuth } from "../lib/auth";

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

export default function CollaborationPanel() {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const [inviteCode, setInviteCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemErr, setRedeemErr] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [sending, setSending] = useState(false);

  const [collaborators, setCollaborators] = useState([]);
  const [collabLoading, setCollabLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadCollaborators = useCallback(async () => {
    if (!user?.org_id) return;
    setCollabLoading(true);
    try {
      const r = await api.get(`/org-admin/${user.org_id}/collaborators`);
      setCollaborators(r.data || []);
    } catch (e) {
      console.warn("[CollaborationPanel] load collaborators failed:", formatErr(e));
    } finally {
      setCollabLoading(false);
    }
  }, [user?.org_id]);

  useEffect(() => {
    if (!user?.org_id) return;
    api.get(`/org-admin/${user.org_id}/collaboration-code`)
      .then((r) => setCode(r.data.code))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
    loadCollaborators();
  }, [user?.org_id, loadCollaborators]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Clipboard blocked."); }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSending(true);
    try {
      await api.post(`/org-admin/${user.org_id}/collaboration/send-invite`, { to_email: inviteEmail.trim() });
      toast.success(`Invite sent to ${inviteEmail.trim()}.`);
      setInviteEmail("");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSending(false);
    }
  };

  const redeem = async () => {
    setRedeeming(true); setRedeemErr("");
    try {
      const r = await api.post("/org-admin/collaboration/redeem", { code: inviteCode.trim().toUpperCase() });
      toast.success(`Now collaborating with ${r.data.partner_org_name}.`);
      setInviteCode("");
      loadCollaborators();
    } catch (e) {
      setRedeemErr(formatErr(e));
    } finally {
      setRedeeming(false);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const activeRows = useMemo(
    () => collaborators.filter((c) => (c.status || "active") === "active"),
    [collaborators],
  );
  const terminatedRows = useMemo(
    () => collaborators.filter((c) => c.status === "terminated"),
    [collaborators],
  );

  const allSelected = activeRows.length > 0 && activeRows.every((r) => selected.has(r.relationship_id));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(activeRows.map((r) => r.relationship_id)));
  };

  const revokeSelected = async () => {
    const targets = activeRows.filter((r) => selected.has(r.relationship_id));
    if (targets.length === 0) return;
    if (!window.confirm(`Revoke ${targets.length} collaboration${targets.length === 1 ? "" : "s"}? They lose access immediately.`)) return;
    setBulkBusy(true);
    try {
      await Promise.all(targets.map((r) =>
        api.patch(`/org-admin/${user.org_id}/collaborators/${r.other_org_id}/revoke`),
      ));
      toast.success(`Revoked ${targets.length} collaboration${targets.length === 1 ? "" : "s"}.`);
      setSelected(new Set());
      await loadCollaborators();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="surface p-5 space-y-6" data-testid="collaboration-panel">
      <div>
        <div className="label-xs mb-1">COLLABORATION</div>
        <div className="text-sm text-muted-foreground">
          Share your <strong>collaboration code</strong> with another organization so they can partner with you.
          Codes are 8 random characters — keep them private; anyone who has yours can request to collaborate.
        </div>
      </div>

      {/* 1. My code */}
      <div>
        <div className="label-xs mb-1.5">YOUR ORGANIZATION&apos;S CODE</div>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={loading ? "Loading…" : code}
            className="input mono text-lg tracking-widest flex-1"
            data-testid="collab-code-display"
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={copy}
            disabled={loading || !code}
            data-testid="collab-code-copy"
          >
            <Copy size={14} /> {copied ? "COPIED" : "COPY"}
          </button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-1">
          Share this with collaborators directly (chat, email). Don&apos;t post it publicly.
        </div>
      </div>

      {/* 2. Send invite by email (inline) */}
      <div>
        <div className="label-xs mb-1.5 flex items-center gap-1.5">
          <PaperPlaneTilt size={12} /> SEND INVITE BY EMAIL
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Emails this code using the <em>Collaboration invite</em> template — manage the message in <strong>Templates → Email</strong>.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="email"
            className="input flex-1"
            placeholder="recipient@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            data-testid="collab-send-email-input"
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={sendInvite}
            disabled={sending || !inviteEmail.includes("@") || !code}
            data-testid="collab-send-email-submit"
          >
            {sending ? "SENDING…" : <>SEND INVITE <ArrowRight size={12} /></>}
          </button>
        </div>
      </div>

      <div className="border-t border-border" />

      {/* 3. Redeem a partner's code */}
      <div>
        <div className="label-xs mb-1.5 flex items-center gap-1.5">
          <Handshake size={12} /> REDEEM A PARTNER&apos;S CODE
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Enter the 8-character code another organization shared with you. We&apos;ll instantly create a partnership.
        </p>
        <Field label="PARTNER'S COLLABORATION CODE">
          <div className="flex items-center gap-2">
            <input
              className="input mono uppercase tracking-widest text-base flex-1"
              maxLength={8}
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              placeholder="XXXX-XXXX"
              data-testid="collab-invite-input"
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={redeem}
              disabled={redeeming || inviteCode.length !== 8}
              data-testid="collab-invite-submit"
            >
              {redeeming ? "CONNECTING…" : <>REDEEM <ArrowRight size={12} /></>}
            </button>
          </div>
        </Field>
        {redeemErr && (
          <div className="text-xs text-destructive mt-2" data-testid="collab-invite-error">{redeemErr}</div>
        )}
      </div>

      <div className="border-t border-border" />

      {/* 4. Collaborators table */}
      <div data-testid="collab-collaborators-section">
        <div className="flex items-center justify-between mb-1.5">
          <div className="label-xs">ORGANIZATIONS COLLABORATING WITH YOU</div>
          {selected.size > 0 && (
            <button
              type="button"
              className="btn btn-secondary text-xs"
              onClick={revokeSelected}
              disabled={bulkBusy}
              data-testid="collab-bulk-revoke"
            >
              <X size={12} /> {bulkBusy ? "REVOKING…" : `REVOKE SELECTED (${selected.size})`}
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Both your incoming partners (redeemed your code) and outgoing partners (you redeemed theirs) appear here.
        </p>

        {collabLoading && (
          <div className="text-xs text-muted-foreground" data-testid="collab-collaborators-loading">Loading collaborators…</div>
        )}

        {!collabLoading && activeRows.length === 0 && terminatedRows.length === 0 && (
          <div className="text-xs text-muted-foreground italic" data-testid="collab-collaborators-empty">
            No organizations are collaborating with you yet.
          </div>
        )}

        {(activeRows.length > 0 || terminatedRows.length > 0) && (
          <div className="overflow-x-auto rounded-md border border-border" data-testid="collab-table-wrap">
            <table className="w-full text-sm" data-testid="collab-table">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-10 px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={activeRows.length === 0}
                      aria-label="Select all"
                      data-testid="collab-table-select-all"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Company name</th>
                  <th className="px-3 py-2 text-left">City, State</th>
                  <th className="px-3 py-2 text-left">Last interaction</th>
                  <th className="px-3 py-2 text-left">Partnered since</th>
                </tr>
              </thead>
              <tbody>
                {activeRows.map((c) => {
                  const isSel = selected.has(c.relationship_id);
                  const cityState = [c.city, c.state].filter(Boolean).join(", ") || "—";
                  return (
                    <tr
                      key={c.relationship_id}
                      className={`border-t border-border ${isSel ? "bg-primary/5" : ""}`}
                      data-testid={`collab-row-${c.other_org_id}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleRow(c.relationship_id)}
                          aria-label={`Select ${c.other_org_name}`}
                          data-testid={`collab-row-check-${c.other_org_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium truncate" data-testid={`collab-row-name-${c.other_org_id}`}>
                        {c.other_org_name}
                        <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {c.direction === "incoming" ? "← redeemed" : "→ you redeemed"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{cityState}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtDate(c.partnered_since)}</td>
                      <td className="px-3 py-2">{fmtDate(c.partnered_since)}</td>
                    </tr>
                  );
                })}
                {terminatedRows.map((c) => {
                  const cityState = [c.city, c.state].filter(Boolean).join(", ") || "—";
                  return (
                    <tr
                      key={c.relationship_id}
                      className="border-t border-border opacity-60"
                      data-testid={`collab-row-terminated-${c.other_org_id}`}
                    >
                      <td className="px-3 py-2 text-muted-foreground text-[10px] uppercase">revoked</td>
                      <td className="px-3 py-2 line-through">{c.other_org_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{cityState}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtDate(c.partnered_since)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtDate(c.partnered_since)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
